<template>
  <div class="admin-container">
    <!-- 现有视图切换和头部操作 -->
    <div class="view-toggle">
      <button @click="isManagementView = true" :class="{active: isManagementView}">管理视图</button>
    </div>

    <div class="header-actions">
      <h1>{{ isManagementView ? '产品管理' : '产品中心' }}</h1>
      <button v-if="isManagementView && isAdmin" @click="$router.push('/admin/products/add')" class="btn-add">添加产品</button>
    </div>

    <!-- 管理视图内容 -->
    <ProductFilter @search="handleSearch" />
    <!-- 移除热区编辑按钮绑定 -->
    <div class="table-container">
      <ProductTable 
        :products="products"
        :loading="loading"
        @edit="handleEdit"
        @delete="handleDeleteConfirm"
        :columns="['title', 'description', 'imageUrls', 'techSpecs', 'dimensionImageUrl', 'actions']"
      />
    </div>

    <!-- 确认删除对话框 -->
    <div v-if="showDeleteDialog" class="dialog-overlay">
      <div class="dialog">
        <h3>确认删除</h3>
        <p>确定要删除产品「{{ currentProduct?.title }}」吗？此操作不可撤销。</p>
        <div class="dialog-actions">
          <button @click="handleDeleteCancel" class="btn-cancel">取消</button>
          <button @click="handleDelete" class="btn-delete">确认删除</button>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loading-overlay">...</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getAllProducts, deleteProduct } from '@/api/product'; // 移除updateProductComponents导入
import ProductTable from '@/components/admin/ProductTable.vue';
import ProductFilter from '@/components/admin/ProductFilter.vue';

// 原有状态变量
const isAdmin = ref(true);
const isManagementView = ref(true);
const products = ref([]);
const loading = ref(false);
const showDeleteDialog = ref(false);
const currentProduct = ref(null);
const router = useRouter();

// 加载产品列表
const loadProducts = async () => {
  try {
    loading.value = true;
    const response = await getAllProducts();
    products.value = response.data;
  } catch (error) {
    console.error('加载产品失败:', error);
    alert('加载产品失败，请刷新页面重试');
  } finally {
    loading.value = false;
  }
};

// 保留搜索、编辑、删除等管理功能
const handleSearch = async (searchParams) => {
  try {
    loading.value = true;
    // 这里根据实际筛选条件实现搜索逻辑
    const response = await getAllProducts();
    products.value = response.data.filter(product => {
      // 标题模糊搜索
      const matchesTitle = product.title.toLowerCase().includes(searchParams.keyword.toLowerCase());
      // 标签筛选
      const matchesTag = !searchParams.tag || product.tag === searchParams.tag;
      // 状态筛选
      const matchesStatus = searchParams.status === 'all' || 
                           (searchParams.status === 'active' && product.isActive) ||
                           (searchParams.status === 'inactive' && !product.isActive);
      return matchesTitle && matchesTag && matchesStatus;
    });
  } catch (error) {
    console.error('搜索产品失败:', error);
    alert('搜索产品失败，请重试');
  } finally {
    loading.value = false;
  }
};

// 编辑产品
const handleEdit = (productId) => {
  router.push(`/admin/products/edit/${productId}`);
};

// 确认删除产品
const handleDeleteConfirm = (product) => {
  currentProduct.value = product;
  showDeleteDialog.value = true;
};

// 取消删除
const handleDeleteCancel = () => {
  showDeleteDialog.value = false;
  currentProduct.value = null;
};

// 执行删除
const handleDelete = async () => {
  if (!currentProduct.value) return;

  try {
    loading.value = true;
    await deleteProduct(currentProduct.value.id);
    // 删除成功后重新加载列表
    await loadProducts();
    showDeleteDialog.value = false;
    alert('产品删除成功');
  } catch (error) {
    console.error('删除产品失败:', error);
    alert('删除产品失败，请重试');
  } finally {
    loading.value = false;
    currentProduct.value = null;
  }
};

onMounted(() => {
  loadProducts();
});
</script>

<style scoped lang="scss">

@import '@/styles/sections/admin/productlist/hotspot';

.admin-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .btn-add {
    background-color: #42b983;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  
  .btn-add:hover {
    background-color: #359469;
  }
  
  .table-container {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: hidden;
  }
  
  /* 对话框样式 */
  .dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .dialog {
    background-color: white;
    padding: 24px;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
  }
  
  .dialog h3 {
    margin-top: 0;
    color: #333;
  }
  
  .dialog p {
    color: #666;
    margin-bottom: 24px;
  }
  
  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  
  .btn-cancel {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .btn-delete {
    background-color: #ff4444;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  /* 加载中样式 */
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255,255,255,0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
  }
  
  .loading-spinner {
    font-size: 18px;
    color: #666;
  }
  
  @media (max-width: 768px) {
    .header-actions {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  }

/* 视图切换样式 */
.view-toggle {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.view-toggle button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
}

.view-toggle button.active {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

/* 产品分类样式 */
.product-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 20px 0;
}

.category-item {
  padding: 8px 16px;
  border-radius: 20px;
  background-color: #f5f5f5;
  cursor: pointer;
}

.category-item.active {
  background-color: #42b983;
  color: white;
}

/* 产品网格样式 */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 20px;
}

.product-item {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s;
}

.product-item:hover {
  transform: translateY(-5px);
}

.product-item img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.product-name {
  padding: 10px;
  text-align: center;
}

.product-actions {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  background-color: #f9f9f9;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: 20px 0;
}

.page-btn {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
}

.page-btn.active {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 新增：图片选择器样式 */
.image-selector {
  margin-bottom: 15px;
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 5px;
}

.image-thumbnail {
  width: 60px;
  height: 60px;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.image-thumbnail.active {
  border-color: #42b983;
}

.image-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
